import React, { useState, ReactNode, useEffect } from 'react';
import { BirdzModal, BirdzModalContent } from '@applications-terrains/birdz-react-library';
import { Backdrop, Box, Button, CircularProgress, Divider, Fade } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import Warning from '@mui/icons-material/Warning';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import styled, { keyframes } from 'styled-components';
import { red, blue, orange } from '@mui/material/colors';
import LinearProgress from '@mui/material/LinearProgress';
import { removeAccents } from '../../../utils';
import { t } from 'i18next';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';

export type RequestStatus =
  'success' | 'pending' | 'error' | 'successWithNoResults' | 'warning' | null;
export type ModalWithLoaderAction = 'edit' | 'duplicate' | 'add' | 'delete' | null;

/** Hauteur réservée au contenu, dans laquelle l'overlay de chargement se centre. */
export const MODAL_WITH_LOADER_MIN_HEIGHT = 365;

type ModalWithLoaderType = {
  children?: ReactNode;
  onClose?: () => void;
  openModal: boolean;
  action?: string;
  status: RequestStatus;
  setStatus?: (status: RequestStatus) => void;
  customErrorMessage?: ReactNode;
  customSuccessMessage?: ReactNode;
  customWarningMessage?: ReactNode;
  timeout?: number;
  loaderTimeout?: number;
  onSuccess?: () => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false; // Ajoutez cette ligne
  fullWidth?: boolean; // Ajoutez cette ligne
  header?: ReactNode;
  footer?: ReactNode;
  showCloseButton?: boolean;
  contentMinHeight?: number;
};

/**
 * ModalWithLoader component that displays a modal with loading states and overlay functionality.
 *
 * This component manages the opening and closing of a modal based on external state changes,
 * and provides loading, success, and error states through an overlay system.
 *
 * @param children - The content to be displayed inside the modal
 * @param onClose - Callback function triggered when the modal is closed
 * @param status - Current status of the modal operation ('loading', 'success', 'error', 'successWithNoResults', or null)
 * @param setStatus - Function to update the status state
 * @param openModal - Boolean flag to control whether the modal should be open
 * @param action - Text describing the action being performed (default: 'Enregistrement')
 * @param customErrorMessage - Custom error message to display when status is 'error'
 * @param timeout - Delay in milliseconds before auto-closing the modal on success/error (default: 1500)
 * @param onSuccess - Callback function triggered when the operation completes successfully
 * @param contentMinHeight - Minimum height of the content, in which the overlay is centered (default: MODAL_WITH_LOADER_MIN_HEIGHT)
 *
 * @returns JSX element representing the modal, or null if no children and no status
 *
 * @remarks
 * The component uses a conditional render check to prevent displaying an empty modal.
 * It only renders when there are valid children components OR when there's an active status,
 * ensuring the modal doesn't appear unnecessarily when both children and status are null/empty.
 */
const ModalWithLoader = ({
  children,
  onClose,
  status,
  setStatus,
  openModal,
  action = 'Enregistrement',
  customErrorMessage,
  customSuccessMessage,
  customWarningMessage,
  timeout = 1500,
  onSuccess,
  maxWidth,
  header,
  footer,
  showCloseButton,
  contentMinHeight = MODAL_WITH_LOADER_MIN_HEIGHT
}: ModalWithLoaderType) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (openModal) {
      setIsOpen(true);
      return;
    }

    if (status === 'successWithNoResults') {
      return;
    }

    // Le timer est annulé au démontage et à chaque changement de dépendances :
    // sans cela il se déclenche sur un composant démonté, et chaque rendu
    // empile une fermeture supplémentaire.
    const closeTimer = setTimeout(
      () => {
        setIsOpen(false);
        setStatus && setStatus(null);
        status === 'success' && onSuccess && onSuccess();
      },
      status === 'success' || status === 'error' ? timeout : 300
    );

    return () => clearTimeout(closeTimer);
  }, [openModal, status, onSuccess, setStatus, timeout]);

  // Filter out null or undefined children to avoid rendering empty modals
  // This ensures that the modal only opens if there are valid children or a status to display.
  return React.Children.toArray(children).filter((child) => child !== null && child !== undefined)
    .length > 0 || status !== null ? (
    <BirdzModal
      maxWidth={maxWidth || 'sm'}
      fullWidth={true}
      open={openModal}
      closeAfterTransition
      onClose={onClose}
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 300
        }
      }}
    >
      <Fade in={isOpen}>
        <Box overflow={'hidden'} position="relative">
          {/* Bouton de fermeture */}
          {showCloseButton && (
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1202,
                '&:hover': {
                  backgroundColor: 'grey.100'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          )}

          {header}
          <BirdzModalContent>
            <Box sx={{ minHeight: contentMinHeight, maxHeight: '80vh' }}>
              {children}
              <Overlay
                timeout={timeout}
                action={action}
                status={status}
                isOpen={openModal}
                onClose={() => {
                  setIsOpen(false);
                  setStatus && setStatus(null);
                  onClose && onClose();
                }}
                customErrorMessage={customErrorMessage}
                customSuccessMessage={customSuccessMessage}
                customWarningMessage={customWarningMessage}
                showCloseButton={showCloseButton}
              />
            </Box>
          </BirdzModalContent>
          {footer}
        </Box>
      </Fade>
    </BirdzModal>
  ) : null;
};

export default ModalWithLoader;

const blueColor = blue[300];
const redColor = red[400];
const orangeColor = orange[300];

const Overlay = ({
  action,
  status,
  isOpen,
  onClose,
  customErrorMessage,
  customSuccessMessage,
  customWarningMessage,
  timeout,
  showCloseButton
}: {
  action: string;
  status: RequestStatus;
  isOpen: boolean;
  onClose: () => void;
  customErrorMessage?: ReactNode;
  customSuccessMessage?: ReactNode;
  customWarningMessage?: ReactNode;
  timeout: number;
  showCloseButton?: boolean;
}) => {
  const getFeminine = () => {
    const feminineWords = [
      'creation',
      'modification',
      'suppression',
      'edition',
      'action',
      'activation',
      'desactivation',
      'cloture',
      'recuperation',
      'duplication'
    ];
    return action
      .split(' ')
      .map((word) => (feminineWords.includes(removeAccents(word.toLowerCase())) ? 'e' : ''))
      .filter((el) => el)
      .join();
  };

  const data = {
    success: {
      message:
        action +
        (typeof customSuccessMessage === 'string'
          ? customSuccessMessage
          : t(getFeminine() ? 'doneSuccessfullyFem' : 'doneSuccessfully')),
      icon: <SuccessIcon />
    },
    successWithNoResults: {
      message: customWarningMessage ?? t('noResultsFound'),
      icon: <WarningIcon />
    },
    pending: {
      message: action + t(' in progress... Please wait.'),
      icon: <CircularProgress sx={{ mb: 3, fontSize: '1.2rem' }} />
    },
    error: {
      message: customErrorMessage || t('anErrorOccurred'),
      icon: <ErrorIcon />
    },
    warning: {
      message: customWarningMessage ?? t('warning'),
      icon: <WarningIcon />
    }
  };
  const [isVisible, setIsVisible] = useState(false);
  const [tmpStatus, setTmpStatus] = useState<RequestStatus>(null);
  const [showProgressBar, setShowProgressBar] = useState(false);

  const handleClose = () => {
    setTmpStatus(null);
    setIsVisible(false);
    setShowProgressBar(false);
  };

  useEffect(() => {
    if (status === null) {
      handleClose();
    }
    if (status !== null) {
      setIsVisible(true);
      setTmpStatus(status);
    }
    if (status === 'success') setShowProgressBar(true);
    if (status === 'warning') setShowProgressBar(true);
    // if (status !== 'successWithNoResults' && (!isOpen || status === 'error')) {
    //   setShowProgressBar(true);
    //   setTimeout(() => {
    //     handleClose();
    //   }, 5000);
    // }
    if (status === 'successWithNoResults' && isOpen) {
      setShowProgressBar(true);
      setTimeout(() => {
        handleClose();
        onClose();
      }, timeout + 200);
    }
  }, [isOpen, status, timeout]);

  return tmpStatus !== null ? (
    <Box
      sx={{
        color: 'rgba(0, 0, 0, 0.54)',
        fontWeight: 300,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 1)',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out'
      }}
    >
      <div className="d-flex flex-column align-items-center">
        {data[status as keyof typeof data]?.icon}
        <Box textAlign={'center'} maxWidth={400} sx={{ whiteSpace: 'pre-line' }}>
          {data[status as keyof typeof data]?.message}
        </Box>
        {(status === 'error' || status === 'warning') && !showCloseButton && (
          <Divider sx={{ width: '100%', mt: 2, borderBottomWidth: 2 }} />
        )}
        {showProgressBar && (
          <LinearDeterminate
            color={
              status === 'error'
                ? 'red'
                : status === 'successWithNoResults' || status === 'warning'
                  ? 'orange'
                  : 'blue'
            }
            timeout={timeout}
          />
        )}
        {typeof customSuccessMessage === 'object' && status === 'success' && customSuccessMessage}
        {/* {status === 'successWithNoResults' && (
          <Button
            sx={{ mt: 3, color: orangeColor }}
            color="warning"
            onClick={() => {
              onClose();
              setTmpStatus(null);
              setIsVisible(false);
            }}
          >
            modifier ma recherche
          </Button>
        )} */}
        {status === 'error' && !showCloseButton && (
          <Button
            color="error"
            sx={{ mt: 3 }}
            onClick={() => {
              handleClose();
              onClose();
            }}
          >
            {t('tryAgain')}
          </Button>
        )}
        {status === 'warning' && !showCloseButton && (
          <Button
            color="warning"
            sx={{ mt: 3, color: orangeColor }}
            onClick={() => {
              handleClose();
              onClose();
            }}
          >
            {t('close')}
          </Button>
        )}
      </div>
    </Box>
  ) : (
    <></>
  );
};

const expandWidth = keyframes`
0% { width: 0px; }
100% {width: 55px; }
`;
const StyledSuccessIcon = styled.div`
  overflow: hidden;
  width: 55px;
  animation-name: ${expandWidth};
  animation-duration: 0.7s;
`;

const SuccessIcon = () => {
  return (
    <Box sx={{ width: '55px', height: '55px', mb: 3 }}>
      <StyledSuccessIcon>
        <CheckIcon sx={{ fontSize: '55px', color: blueColor }} />
      </StyledSuccessIcon>
    </Box>
  );
};

const expandHeight = keyframes`
  0% { height: 0px; }
  100% {height: 55px; }
 `;

const StyledErrorIcon = styled.div`
  overflow: hidden;
  width: 55px;
  animation-name: ${expandHeight};
  animation-duration: 0.2s;
`;

const ErrorIcon = () => {
  return (
    <Box sx={{ width: '55px', height: '55px', mb: 3 }}>
      <StyledErrorIcon>
        <PriorityHighIcon sx={{ fontSize: '55px', color: redColor }} />
      </StyledErrorIcon>
    </Box>
  );
};

const WarningIcon = () => {
  return (
    <Box sx={{ width: '55px', height: '55px', mb: 3 }}>
      <StyledErrorIcon>
        <Warning sx={{ fontSize: '55px', color: orangeColor }} />
      </StyledErrorIcon>
    </Box>
  );
};

const LinearDeterminate = ({
  color,
  timeout
}: {
  color: 'red' | 'blue' | 'orange';
  timeout: number;
}) => {
  const [progress, setProgress] = useState<number>(0);

  const colors = {
    red: {
      secondary: red[100],
      main: redColor
    },
    blue: {
      secondary: blue[100],
      main: blueColor
    },
    orange: {
      secondary: orange[100],
      main: orangeColor
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        return oldProgress < 100 ? oldProgress + timeout / timeout : 100;
      });
    }, timeout / 180);
    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 4,
          borderRadius: 2,
          backgroundColor: colors[color].secondary,
          '& .MuiLinearProgress-bar': {
            borderRadius: 2,
            backgroundColor: colors[color].main
          }
        }}
      />
    </Box>
  );
};
